import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixMultilingualSitetranslatorV2SiteTranslatableProperties from './multilingual-sitetranslator-v2-site-translatable-properties-site-translator.http.js';

export interface SiteTranslatableProperties {
  /** Total number of words that exist in the main_language */
  totalWords?: number;
  /** Number of words that were already translated into the translated_language */
  translatedWords?: number;
  /** Word credits already used for machine translation */
  machineTranslationWordsUsed?: number;
  /** Word credits available for machine translation */
  machineTranslateWordsLimit?: number;
  /**
   * Word information by app_id
   * @maxSize 1024
   */
  applicationProperties?: ApplicationTranslatableProperties[];
}

export interface ApplicationTranslatableProperties {
  /**
   * Unique identifier of application ID
   * @format GUID
   */
  appId?: string;
  /** Total number of words that exist in the main_language in a specific application */
  applicationTotalWords?: number;
  /** Number of words that were already translated into the translated_language in a specific application */
  applicationTranslatedWords?: number;
}

export interface TranslateSiteRequest {
  /** Language from which the site content is translated */
  mainLanguage: Locale;
  /** Language into which the site content is translated */
  translatedLanguage: Locale;
}

export interface Locale {
  /**
   * The IETF BCP 47 formatted identifier of the language
   * @format LANGUAGE_TAG
   */
  language?: string;
  /** @maxLength 2 */
  flagV2?: string | null;
}

export interface TranslateSiteResponse {}

export interface GetSiteTranslatablesPropertiesRequest {
  /** Number of words included in main_language */
  mainLanguage: Locale;
  /** Number of words already translated from main_language to translated_language */
  translatedLanguage?: Locale;
}

export interface GetSiteTranslatablesPropertiesResponse {
  /** Current status of site translation and word credits usage */
  data?: SiteTranslatableProperties;
}

/**
 * Start a task to auto-translate all site content that hasn't been translated yet
 * @param mainLanguage - Language from which the site content is translated
 * @public
 * @documentationMaturity preview
 * @requiredField mainLanguage
 * @requiredField options
 * @requiredField options.translatedLanguage
 * @permissionId WIX_MULTILINGUAL.ONE_CLICK_TRANSLATION
 * @fqn com.wixpress.multilingual.sitetranslator.v2.SiteTranslatorV2.TranslateSite
 */
export async function translateSite(
  mainLanguage: Locale,
  options: NonNullablePaths<TranslateSiteOptions, `translatedLanguage`, 2>
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    mainLanguage: mainLanguage,
    translatedLanguage: options?.translatedLanguage,
  });

  const reqOpts =
    ambassadorWixMultilingualSitetranslatorV2SiteTranslatableProperties.translateSite(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          mainLanguage: '$[0]',
          translatedLanguage: '$[1].translatedLanguage',
        },
        singleArgumentUnchanged: false,
      },
      ['mainLanguage', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface TranslateSiteOptions {
  /** Language into which the site content is translated */
  translatedLanguage: Locale;
}

/**
 * Receive the site's translation status
 * @param mainLanguage - Number of words included in main_language
 * @public
 * @documentationMaturity preview
 * @requiredField mainLanguage
 * @permissionId WIX_MULTILINGUAL.ONE_CLICK_TRANSLATION
 * @fqn com.wixpress.multilingual.sitetranslator.v2.SiteTranslatorV2.GetSiteTranslatablesProperties
 */
export async function getSiteTranslatablesProperties(
  mainLanguage: Locale,
  options?: GetSiteTranslatablesPropertiesOptions
): Promise<
  NonNullablePaths<
    GetSiteTranslatablesPropertiesResponse,
    | `data.totalWords`
    | `data.translatedWords`
    | `data.machineTranslationWordsUsed`
    | `data.machineTranslateWordsLimit`
    | `data.applicationProperties`
    | `data.applicationProperties.${number}.appId`
    | `data.applicationProperties.${number}.applicationTotalWords`
    | `data.applicationProperties.${number}.applicationTranslatedWords`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    mainLanguage: mainLanguage,
    translatedLanguage: options?.translatedLanguage,
  });

  const reqOpts =
    ambassadorWixMultilingualSitetranslatorV2SiteTranslatableProperties.getSiteTranslatablesProperties(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          mainLanguage: '$[0]',
          translatedLanguage: '$[1].translatedLanguage',
        },
        singleArgumentUnchanged: false,
      },
      ['mainLanguage', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetSiteTranslatablesPropertiesOptions {
  /** Number of words already translated from main_language to translated_language */
  translatedLanguage?: Locale;
}
